import { SideNavigationProps } from '@amzn/awsui-components-core';
import { useMemo } from 'react';
import { useAuth } from '../../context/AuthContextProvider';
import { getAdminNavigationItems } from '../../../utilities/RouteConfig';
import { NavItem } from '../../side-navigation-v2/useNavigationItems';

/**
 * Transforms Cloudscape SideNavigationProps.Item[] (including 'section' types)
 * into our flat NavItem[] format for the V2 side navigation.
 */
const transformAdminItems = (items: SideNavigationProps.Item[]): NavItem[] => {
  return items.reduce<NavItem[]>((acc, item) => {
    if (item.type === 'divider') {
      acc.push({ text: '', href: '', type: 'divider' });
    } else if (item.type === 'link') {
      acc.push({
        text: item.text,
        href: item.href || '',
        external: item.external,
        type: 'link'
      });
    } else if (item.type === 'expandable-link-group') {
      acc.push({
        text: item.text,
        href: item.href,
        type: 'expandable-group',
        defaultExpanded: item.defaultExpanded,
        children: transformAdminItems([...item.items])
      });
    } else if (item.type === 'section') {
      // Add divider before section (except for the first item)
      if (acc.length > 0) {
        acc.push({ text: '', href: '', type: 'divider' });
      }
      // Add section header
      acc.push({
        text: item.text,
        href: '',
        type: 'section-header',
        bold: true
      });
      // Add section child items as flat links (indented under section header)
      if (item.items) {
        item.items.forEach((child) => {
          if (child.type === 'link') {
            acc.push({
              text: child.text,
              href: child.href || '',
              external: child.external,
              type: 'link',
              indented: true
            });
          }
        });
      }
    }
    return acc;
  }, []);
};

/**
 * Hook that builds admin navigation items and returns them
 * in the simplified NavItem[] format used by the V2 side navigation.
 */
export const useAdminNavigationItems = (): NavItem[] => {
  const userAuthenticationDetails = useAuth();

  const navItems = useMemo(() => {
    const adminItems = getAdminNavigationItems(userAuthenticationDetails.userLDAPGroups);
    return transformAdminItems(adminItems);
  }, [userAuthenticationDetails.userLDAPGroups]);

  return navItems;
};
